// The control panel: Tweakpane, dressed so a newcomer can actually use it.
//
// Three things this file tries to be:
//
//   1. LEGIBLE. Every control has a human label, a unit, and a one-line
//      explanation of which direction does what. Tweakpane has no tooltips, so
//      the explanation is a small DOM line inserted after each blade (see
//      `after()` / `helpEl()`); a header button hides them all once you know
//      the panel.
//   2. SCENARIO-FIRST. Nobody learns an ecosystem by dragging one slider. The
//      preset chips snap the WHOLE run (params + energy economy) to a
//      configuration that demonstrates something — no wolves, harsh winters,
//      rescue floors off — and restart, because that is the only honest way to
//      change founding conditions.
//   3. COMPLETE. econ.ts is where the biology lives (the price of a brain, of
//      eyes, of sprint muscle) and it used to be unreachable from the browser.
//      It is now an advanced section that posts a patch through `onEcon`.
//
// Because the sim lives in a worker, nothing here mutates it directly: edits
// fire `onChange` (params/run state) or `onEcon` (economy) and main.ts forwards
// the current state to the worker.

import { Pane } from "tweakpane";
import type { BindingParams, FolderApi } from "tweakpane";
import { DEFAULT_PARAMS, type SimParams } from "../config";
import { DEFAULT_ECON, type Econ } from "../sim/econ";
import { lifetimeSecondsAt } from "../sim/scale";

/** Seconds → "12 min", "45 s", "1.4 h" — for explaining what a speed means. */
function humanDuration(s: number): string {
  if (!Number.isFinite(s) || s <= 0) return "instant";
  if (s < 1) return `${s.toFixed(1)} s`;
  if (s < 90) return `${Math.round(s)} s`;
  if (s < 5400) return `${Math.round(s / 60)} min`;
  return `${(s / 3600).toFixed(1)} h`;
}

export interface RunState {
  running: boolean;
  /**
   * How fast the world runs RELATIVE TO REAL LIFE. 1 = life-like motion,
   * 25 = twenty-five times faster, 0 = unlimited (as fast as one core manages).
   *
   * Expressed this way rather than as ticks/second because movement in this sim
   * is literal — see scale.ts — so the multiplier means something a person can
   * picture. main.ts converts it to a tick rate for the worker.
   */
  speedMult: number;
  seed: number;
}

export interface ControlCallbacks {
  onChange: () => void; // any param / run-state binding changed
  onRestart: () => void;
  onStep: () => void;
  onSave: (name: string) => void;
  onLoad: () => void;
  /**
   * The energy economy changed. `patch` is a complete flat snapshot of the
   * live `Econ` object (every field, all numbers), so the receiver can forward
   * it verbatim: `worker.postMessage({ type: "econ", patch })`, and the worker
   * applies it with `setEcon(patch)`.
   *
   * Optional ONLY so this file compiles against a main.ts that has not wired it
   * up yet — treat it as required. Without it the whole "Energy economy"
   * section is inert.
   */
  onEcon?: (patch: Record<string, number>) => void;
}

// --- Presets -----------------------------------------------------------------
// A preset is a WHOLE configuration, not a nudge: applying one resets params and
// economy to the shipped defaults and then lays its own patch on top, so the
// same chip always produces the same world. The seed is deliberately left alone
// so scenarios can be compared on identical ground.

interface Preset {
  name: string;
  blurb: string;
  params?: Partial<SimParams>;
  econ?: Partial<Econ>;
}

const PRESETS: Preset[] = [
  {
    name: "Balanced",
    blurb:
      "The shipped defaults: 17.3 km² of country, a couple of thousand animals, rescue floors on so the sandbox never ends. This is the steadiest configuration measured — the populations stop ringing once there is enough room that a crash in one valley stays there.",
  },
  {
    name: "Predator pressure",
    blurb:
      "Four packs, and better hunters. Watch the deer answer over a few generations — senses, speed and wariness should all climb, because now they pay for themselves.",
    params: {
      initialHerbivores: 260,
      initialCarnivores: 40,
      packSize: 10,
      minCarnivores: 12,
    },
    econ: { huntBaseOdds: 0.28, huntPackBonus: 0.4 },
  },
  {
    name: "Paradise",
    blurb:
      "No wolves at all, and grass everywhere. Nothing hunts you, so keen senses and sprint muscle stop paying for themselves — expect both to decay.",
    params: {
      initialCarnivores: 0,
      minCarnivores: 0,
      grassRegrowth: 1.8,
      fertilityFloor: 0.45,
      seasonAmplitude: 0.15,
    },
  },
  {
    name: "Harsh seasons",
    blurb:
      "Deep, fast winters. Growth all but stops for part of every year, so survival turns on fat reserves and a lean metabolism.",
    params: {
      seasonAmplitude: 0.95,
      seasonPeriod: 160,
      grassRegrowth: 1.1,
    },
  },
  {
    name: "Lean range",
    blurb:
      "Thin, patchy ground. Grazing worth having is scattered in small pockets with barren stretches between — competition and travel instead of abundance.",
    params: {
      grassRegrowth: 0.6,
      grassMax: 55,
      fertilityFloor: 0.04,
      fertilityPatches: 8,
      initialHerbivores: 140,
    },
  },
  {
    name: "Closed system",
    blurb:
      "Rescue floors off — nothing is topped up. The honest test of whether the ecology stands on its own. It may well go extinct; that is the result.",
    params: { minHerbivores: 0, minCarnivores: 0 },
  },
  {
    // The OLD default, kept as a scenario because the contrast is the single
    // most instructive thing measured in this project. Same seeds, 50k ticks:
    // 1.9 km² runs at CV 0.45-0.83 and 17.3 km² at CV 0.14-0.17. Room, not any
    // economy constant, is what stops the populations ringing.
    name: "Cramped range",
    blurb:
      "The old map — the same ecology squeezed into a ninth of the ground. Watch the population chart: deer and wolves start chasing each other up and down instead of settling. Nothing about the animals changed, only how much country they have.",
    params: {
      worldWidth: 1600,
      worldHeight: 1200,
      initialHerbivores: 180,
      initialCarnivores: 16,
    },
  },
];

// --- Small DOM helpers -------------------------------------------------------

type HasElement = { element: HTMLElement };

// Captions are inserted as SIBLINGS of the blade they describe, which means
// they cannot go in until every blade exists: Tweakpane positions a new blade
// with `container.insertBefore(el, container.children[index])`, so any extra
// node already in the container shifts those indices and the panel comes out
// scrambled. Everything is therefore queued during construction and flushed
// once, after the last `addBinding`.
let pending: [HasElement, HTMLElement][] = [];

/** Queue an element to become the blade's next sibling — see `flushCaptions`. */
function after(api: HasElement, el: HTMLElement): void {
  pending.push([api, el]);
}

function flushCaptions(): void {
  for (const [api, el] of pending) api.element.insertAdjacentElement("afterend", el);
  pending = [];
}

function helpEl(text: string, unit?: string): HTMLElement {
  const d = document.createElement("div");
  d.className = "eco-help";
  if (unit) {
    const u = document.createElement("span");
    u.className = "eco-unit";
    u.textContent = unit;
    d.appendChild(u);
  }
  d.appendChild(document.createTextNode(text));
  return d;
}

interface Knob {
  label: string;
  help: string;
  /** Unit chip shown at the head of the help line ("m", "s", "energy/s"…). */
  unit?: string;
  min: number;
  max: number;
  step: number;
  format?: (v: number) => string;
}

/** Tweakpane binds by key on a plain object; this keeps call sites type-checked. */
function bindable<O extends object>(o: O): Record<string, unknown> {
  return o as unknown as Record<string, unknown>;
}

/** A numeric slider plus its explanation line. */
function knob<O extends object>(f: FolderApi, obj: O, key: keyof O & string, k: Knob): void {
  const p: Record<string, unknown> = { label: k.label, min: k.min, max: k.max, step: k.step };
  if (k.format) p.format = k.format;
  const b = f.addBinding(bindable(obj), key, p as BindingParams);
  after(b, helpEl(k.help, k.unit));
}

/** A checkbox plus its explanation line. */
function toggle<O extends object>(
  f: FolderApi,
  obj: O,
  key: keyof O & string,
  label: string,
  help: string,
): void {
  const b = f.addBinding(bindable(obj), key, { label });
  after(b, helpEl(help));
}

/** A button plus its explanation line. */
function action(f: FolderApi, title: string, help: string, fn: () => void): void {
  const b = f.addButton({ title });
  b.on("click", fn);
  after(b, helpEl(help));
}

/** A folder-level note (no control attached). */
function note(f: FolderApi, text: string): void {
  const anchor = f.addBlade({ view: "separator" });
  anchor.element.classList.add("eco-anchor");
  const d = document.createElement("div");
  d.className = "eco-note";
  d.textContent = text;
  after(anchor, d);
}

const int = (v: number): string => v.toFixed(0);
const one = (v: number): string => v.toFixed(1);
const two = (v: number): string => v.toFixed(2);
const pct = (v: number): string => `${(v * 100).toFixed(0)}%`;

export class Controls {
  pane: Pane;
  private exp = { name: "run-1" };
  private econ: Econ = { ...DEFAULT_ECON };
  private params: SimParams;
  private cb: ControlCallbacks;
  /** True while a preset is writing values, so refresh() doesn't echo back. */
  private applying = false;
  private chips: HTMLButtonElement[] = [];
  private descEl!: HTMLElement;
  private activePreset = 0;

  constructor(params: SimParams, run: RunState, cb: ControlCallbacks) {
    this.params = params;
    this.cb = cb;
    injectStyle();

    const pane = new Pane({ title: "EcoSim — controls" });
    this.pane = pane;
    pane.element.classList.add("eco-pane");

    this.buildHeader(pane);
    this.buildRun(pane, run, cb);
    this.buildScenarios(pane);
    this.buildWorld(pane);
    this.buildLife(pane);
    this.buildEconomy(pane);
    this.buildExperiments(pane, cb);
    flushCaptions();

    // Any binding change → let main push the new state to the worker. Econ
    // bindings additionally post their own patch (see `eknob`).
    pane.on("change", () => {
      if (this.applying) return;
      this.markCustom();
      cb.onChange();
    });

    // On small screens, start collapsed so the panel doesn't cover the canvas;
    // tapping the title expands it.
    if (window.matchMedia("(max-width: 860px)").matches) {
      pane.expanded = false;
    }
  }

  // --- Header ---------------------------------------------------------------

  private buildHeader(pane: Pane): void {
    const btn = pane.addButton({ title: "Hide explanations" });
    btn.on("click", () => {
      const off = pane.element.classList.toggle("eco-nohelp");
      btn.title = off ? "Show explanations" : "Hide explanations";
    });
  }

  // --- Run ------------------------------------------------------------------

  private buildRun(pane: Pane, run: RunState, cb: ControlCallbacks): void {
    const f = pane.addFolder({ title: "Run", expanded: true });

    toggle(
      f,
      run,
      "running",
      "Playing",
      "Uncheck to freeze the world and study it without the clock moving.",
    );
    // Speed is expressed as a REAL-TIME MULTIPLIER, not as ticks per second.
    // Movement in this sim is literal (1 sim-second = 1 real second, see
    // scale.ts), so the multiplier is meaningful rather than arbitrary: at 1x a
    // deer covers ground at the speed a deer covers ground.
    //
    // "Unlimited" is folded in as multiplier 0 rather than living as a separate
    // checkbox — two controls for one concept invited the contradictory state
    // where the slider said 240 and the checkbox overrode it anyway.
    const speedBinding = f.addBinding(bindable(run), "speedMult", {
      label: "Speed",
      options: {
        "1× — real time": 1,
        "2×": 2,
        "5×": 5,
        "10×": 10,
        "25×": 25,
        "50×": 50,
        "100×": 100,
        "250×": 250,
        "500×": 500,
        "1000×": 1000,
        "Unlimited (as fast as one core)": 0,
      },
    });
    const speedHelp = helpEl("", "vs real life");
    after(speedBinding, speedHelp);
    const describeSpeed = (): void => {
      const m = run.speedMult;
      if (m <= 0) {
        speedHelp.textContent =
          "As fast as one CPU core allows — fastest evolution, but motion is too jumpy to watch. Generations pass in seconds.";
      } else {
        speedHelp.textContent =
          `Animals move ${m === 1 ? "at life-like speed" : `${m}× life speed`}` +
          ` — a deer sprint is 64 km/h at 1×. One deer lifetime ≈ ${humanDuration(lifetimeSecondsAt(m))}.` +
          " Note that lifespans are compressed regardless: even at 1× a generation passes in minutes, not years.";
      }
      // helpEl puts the unit chip first; textContent above wipes it, so re-add.
      if (!speedHelp.querySelector(".eco-unit")) {
        const u = document.createElement("span");
        u.className = "eco-unit";
        u.textContent = "vs real life";
        speedHelp.prepend(u);
      }
    };
    describeSpeed();
    speedBinding.on("change", describeSpeed);
    action(f, "Step once", "Advance a single step (0.1 s of world time) while paused.", cb.onStep);

    // A plain number field, not a slider: a seed is an identifier, and any
    // integer is valid.
    const seed = f.addBinding(bindable(run), "seed", { label: "Seed", step: 1, format: int });
    after(
      seed,
      helpEl(
        "The run is fully determined by seed + settings. The same numbers replay the same world, animal for animal.",
      ),
    );
    action(f, "Roll a new seed", "Picks a different world to start from, keeping every setting.", () => {
      this.applying = true;
      run.seed = 1 + Math.floor(Math.random() * 999999);
      this.pane.refresh();
      this.applying = false;
      cb.onChange();
      cb.onRestart();
    });
    action(
      f,
      "Restart with these settings",
      "Rebuilds the world from scratch. Needed for anything marked “restart” — map size, terrain and founding populations.",
      cb.onRestart,
    );
  }

  // --- Scenario presets -----------------------------------------------------

  private buildScenarios(pane: Pane): void {
    const f = pane.addFolder({ title: "Scenarios", expanded: true });
    const anchor = f.addBlade({ view: "separator" });
    anchor.element.classList.add("eco-anchor");

    const block = document.createElement("div");
    block.className = "eco-block";

    const wrap = document.createElement("div");
    wrap.className = "eco-presets";

    PRESETS.forEach((p, i) => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "eco-chip";
      b.textContent = p.name;
      b.addEventListener("click", () => this.applyPreset(i));
      b.addEventListener("pointerenter", () => this.showBlurb(p.blurb));
      b.addEventListener("focus", () => this.showBlurb(p.blurb));
      b.addEventListener("pointerleave", () => this.showBlurb(null));
      b.addEventListener("blur", () => this.showBlurb(null));
      wrap.appendChild(b);
      this.chips.push(b);
    });

    const desc = document.createElement("div");
    desc.className = "eco-desc";
    this.descEl = desc;

    const foot = document.createElement("div");
    foot.className = "eco-note";
    foot.textContent =
      "A scenario replaces every setting below and restarts the run. Your seed is kept, so two scenarios can be compared on identical ground.";

    block.appendChild(wrap);
    block.appendChild(desc);
    block.appendChild(foot);
    after(anchor, block);

    this.setActive(0);
  }

  private showBlurb(text: string | null): void {
    if (text) {
      this.descEl.textContent = text;
      return;
    }
    this.descEl.textContent =
      this.activePreset < 0 ? "Custom settings — no scenario active." : PRESETS[this.activePreset].blurb;
  }

  private setActive(i: number): void {
    this.activePreset = i;
    this.chips.forEach((c, j) => c.classList.toggle("is-active", j === i));
    this.showBlurb(null);
  }

  private markCustom(): void {
    if (this.activePreset !== -1) this.setActive(-1);
  }

  private applyPreset(i: number): void {
    const p = PRESETS[i];
    this.applying = true;
    Object.assign(this.params, DEFAULT_PARAMS, p.params ?? {});
    Object.assign(this.econ, DEFAULT_ECON, p.econ ?? {});
    this.pane.refresh();
    this.applying = false;

    this.setActive(i);
    this.pushEcon();
    this.cb.onChange();
    this.cb.onRestart();
  }

  // --- World ----------------------------------------------------------------

  private buildWorld(pane: Pane): void {
    const f = pane.addFolder({ title: "World", expanded: true });
    const p = this.params;

    knob(f, p, "grassRegrowth", {
      label: "Grass growth",
      unit: "energy/s per cell",
      help: "How fast grazed ground recovers. Too low and the deer starve on a stripped map; too high and they boom, then crash when the wolves catch up.",
      min: 0,
      max: 3,
      step: 0.05,
      format: two,
    });
    knob(f, p, "grassMax", {
      label: "Grass per cell",
      unit: "energy",
      help: "How much a fully-grown cell of the best ground holds. This is the map's ceiling — the total food available at any moment.",
      min: 10,
      max: 200,
      step: 5,
      format: int,
    });
    knob(f, p, "seasonAmplitude", {
      label: "Season strength",
      help: "How far growth swings across the year. 0 is an eternal spring; near 1 the map effectively stops growing every winter.",
      min: 0,
      max: 1,
      step: 0.05,
      format: two,
    });
    knob(f, p, "seasonPeriod", {
      label: "Season length",
      unit: "seconds per year",
      help: "One full growth cycle. Short means rapid boom and bust; long means winters deep enough to matter but rare enough to survive.",
      min: 20,
      max: 600,
      step: 10,
      format: int,
    });

    const t = f.addFolder({ title: "Terrain & map (restart)", expanded: false });
    note(t, "These reshape the ground itself, so they only take effect on Restart.");
    knob(t, p, "fertilityFloor", {
      label: "Poorest ground",
      help: "Fertility of the worst land, 0–1. At 1 the map is uniform and every herd rises and falls together; low values carve out barren stretches that keep survivors alive through a crash.",
      min: 0,
      max: 1,
      step: 0.05,
      format: two,
    });
    knob(t, p, "fertilityPatches", {
      label: "Patch detail",
      help: "How finely the good ground is broken up. Low means a few big meadows worth fighting over; high scatters many small ones.",
      min: 1,
      max: 20,
      step: 1,
      format: int,
    });
    knob(t, p, "grassCell", {
      label: "Grass cell",
      unit: "m",
      help: "The size of one grazeable patch. Smaller is finer-grained and prettier, and costs more to simulate.",
      min: 8,
      max: 60,
      step: 2,
      format: int,
    });
    knob(t, p, "worldWidth", {
      label: "Map width",
      unit: "m",
      help: "One pixel is one metre. The default map is a 1.6 × 1.2 km patch of country.",
      min: 400,
      max: 6000,
      step: 100,
      format: int,
    });
    knob(t, p, "worldHeight", {
      label: "Map height",
      unit: "m",
      help: "A bigger world means longer journeys between herds — and more room for wolf packs to hold separate territories.",
      min: 400,
      max: 6000,
      step: 100,
      format: int,
    });
  }

  // --- Life & genetics ------------------------------------------------------

  private buildLife(pane: Pane): void {
    const f = pane.addFolder({ title: "Life & genetics", expanded: true });
    const p = this.params;

    knob(f, p, "mutationRate", {
      label: "Mutation rate",
      unit: "per gene, per birth",
      help: "The chance each gene is nudged when a pup is conceived. Zero freezes evolution outright; high values swamp inheritance with noise and offspring stop resembling their parents.",
      min: 0,
      max: 0.5,
      step: 0.01,
      format: pct,
    });
    knob(f, p, "mutationScale", {
      label: "Mutation size",
      unit: "fraction of the range",
      help: "How far a mutated gene jumps, measured against the species' whole allowed range. Small steps refine what works; big steps explore, and usually kill.",
      min: 0,
      max: 0.5,
      step: 0.01,
      format: two,
    });

    const s = f.addFolder({ title: "Founding population (restart)", expanded: false });
    note(
      s,
      "Founders are seeded as herds and packs, not scattered. A population spread thinner than its own sense radius never finds a mate and dies without breeding.",
    );
    knob(s, p, "initialHerbivores", {
      label: "Deer at start",
      unit: "animals",
      help: "More deer means more food for wolves and faster grazing pressure on the grass.",
      min: 0,
      max: 2000,
      step: 10,
      format: int,
    });
    knob(s, p, "initialCarnivores", {
      label: "Wolves at start",
      unit: "animals",
      help: "Set to zero for a predator-free world and watch the deer's defences decay.",
      min: 0,
      max: 400,
      step: 2,
      format: int,
    });
    knob(s, p, "herdSize", {
      label: "Deer per herd",
      unit: "animals",
      help: "Deer are dropped in family-sized bands. Smaller bands spread the same population over more of the map.",
      min: 1,
      max: 60,
      step: 1,
      format: int,
    });
    knob(s, p, "packSize", {
      label: "Wolves per pack",
      unit: "animals",
      help: "A pack hunts together, and only its top-ranked female breeds — so for the same number of wolves, bigger packs mean fewer litters and more hunting power per kill.",
      min: 1,
      max: 30,
      step: 1,
      format: int,
    });

    const r = f.addFolder({ title: "Rescue floors", expanded: false });
    note(
      r,
      "Anti-extinction safety net: a species below its floor is topped up, cloning a survivor where one exists. Set both to zero for a genuinely closed system — the honest test, which may end in extinction.",
    );
    knob(r, p, "minHerbivores", {
      label: "Minimum deer",
      unit: "animals",
      help: "Deer are never allowed below this. High values quietly prop up the wolves by feeding them rescue-spawned prey.",
      min: 0,
      max: 60,
      step: 1,
      format: int,
    });
    knob(r, p, "minCarnivores", {
      label: "Minimum wolves",
      unit: "animals",
      help: "Wolves are never allowed below this. A floor of zero is the only way to find out whether the packs can actually feed themselves.",
      min: 0,
      max: 30,
      step: 1,
      format: int,
    });
  }

  // --- Energy economy (advanced) --------------------------------------------

  private pushEcon(): void {
    this.cb.onEcon?.({ ...this.econ } as unknown as Record<string, number>);
  }

  /** An economy slider: same as `knob`, but also posts the patch. */
  private eknob(f: FolderApi, key: keyof Econ & string, k: Knob): void {
    const p: Record<string, unknown> = { label: k.label, min: k.min, max: k.max, step: k.step };
    if (k.format) p.format = k.format;
    const b = f.addBinding(bindable(this.econ), key, p as BindingParams);
    b.on("change", () => {
      if (!this.applying) this.pushEcon();
    });
    after(b, helpEl(k.help, k.unit));
  }

  private buildEconomy(pane: Pane): void {
    const f = pane.addFolder({ title: "Energy economy (advanced)", expanded: false });
    note(
      f,
      "The physics of the world, not the genes: what everything costs and what it pays. Evolution finds strategies inside these numbers, so changing one changes which animal wins. Applies live, no restart.",
    );

    const up = f.addFolder({ title: "Upkeep", expanded: false });
    this.eknob(up, "idleCost", {
      label: "Resting burn",
      unit: "energy/s",
      help: "What an average-sized animal spends per second doing nothing at all. Every other cost is read against this one.",
      min: 0,
      max: 4,
      step: 0.05,
      format: two,
    });
    this.eknob(up, "moveCost", {
      label: "Cost of moving",
      unit: "energy/s at reference speed",
      help: "Locomotion, rising with the square of velocity. Raise it and travel becomes a luxury; lower it and animals roam the whole map cheaply.",
      min: 0,
      max: 12,
      step: 0.1,
      format: one,
    });
    this.eknob(up, "refSpeed", {
      label: "Reference speed",
      unit: "m/s",
      help: "The speed at which moving costs exactly the figure above. Locomotion is priced on absolute velocity, never as a fraction of the animal's own maximum — otherwise being fast would be free.",
      min: 4,
      max: 30,
      step: 0.5,
      format: one,
    });

    const cap = f.addFolder({ title: "Cost of capability", expanded: false });
    note(
      cap,
      "Tissue bills, charged even when the capability goes unused. They exist so no gene is pure benefit: an option that is free to own rails to its ceiling and stops evolving.",
    );
    this.eknob(cap, "brainCost", {
      label: "Brain",
      unit: "energy/s",
      help: "Running a full-sized brain. Neural tissue is the most expensive thing an animal owns — ~2% of a human's mass and ~20% of its resting burn. Raise this and dim, instinctive animals win.",
      min: 0,
      max: 3,
      step: 0.05,
      format: two,
    });
    this.eknob(cap, "senseCost", {
      label: "Sense organs",
      unit: "energy/s",
      help: "Eyes and nose at maximum range, the brain aside. Raise it and the population evolves blinder.",
      min: 0,
      max: 3,
      step: 0.05,
      format: two,
    });
    this.eknob(cap, "senseLoad", {
      label: "Paying attention",
      unit: "energy/s",
      help: "Processing a wide sensory stream with a big brain — the product of the two. This term is what makes “keen senses, dim brain” a real strategy rather than a free lunch.",
      min: 0,
      max: 3,
      step: 0.05,
      format: two,
    });
    this.eknob(cap, "speedCost", {
      label: "Sprint muscle",
      unit: "energy/s",
      help: "Fast-twitch muscle has to be carried and fed at rest. Without this a high top speed is free to own, and every prey animal becomes a sprinter.",
      min: 0,
      max: 3,
      step: 0.05,
      format: two,
    });
    this.eknob(cap, "reserveCost", {
      label: "Fat reserves",
      unit: "energy/s",
      help: "Building and hauling a full tank. Cheap per kilogram — that is what makes fat good storage — but not free, which is why animals don't evolve unlimited reserves.",
      min: 0,
      max: 3,
      step: 0.05,
      format: two,
    });
    this.eknob(cap, "somaticCost", {
      label: "Not ageing",
      unit: "energy/s",
      help: "Continuous self-repair, charged against a long lifespan. Living a long time competes directly with breeding now — raise it and animals live fast and die young.",
      min: 0,
      max: 3,
      step: 0.05,
      format: two,
    });
    this.eknob(cap, "forceCost", {
      label: "Agility",
      unit: "energy/s",
      help: "Neuromuscular power billed at rest, so quick reflexes aren't free either.",
      min: 0,
      max: 3,
      step: 0.05,
      format: two,
    });
    this.eknob(cap, "growthCost", {
      label: "Growing up fast",
      unit: "energy/s",
      help: "Overhead charged to juveniles only, and doubled for an animal that reaches adulthood in half the time. This is the fast-slow life-history trade.",
      min: 0,
      max: 3,
      step: 0.05,
      format: two,
    });

    const eat = f.addFolder({ title: "Feeding & vigilance", expanded: false });
    this.eknob(eat, "grazeRate", {
      label: "Grazing intake",
      unit: "energy/s",
      help: "What a stationary, average-sized deer pulls out of good grass. The deer's entire income.",
      min: 0,
      max: 40,
      step: 0.5,
      format: one,
    });
    this.eknob(eat, "grazeWalkSpeed", {
      label: "Grazing walk",
      unit: "m/s",
      help: "How fast a deer can drift and still crop. It is the run that stops feeding, not motion as such — but set this to zero and a herd has to stand dead still to eat, which starves the founders.",
      min: 0,
      max: 15,
      step: 0.5,
      format: one,
    });
    this.eknob(eat, "grazeMovePenalty", {
      label: "Penalty above a walk",
      help: "How sharply intake falls off once a deer exceeds the walking speed above. At 1.0 a flat-out sprint yields nothing: you can outrun a wolf or you can eat, not both.",
      min: 0,
      max: 1,
      step: 0.05,
      format: two,
    });
    this.eknob(eat, "alarmDistance", {
      label: "Vigilance range",
      unit: "m",
      help: "How close a wolf must be before grazers lift their heads and stop feeding. Set it wider than any sense radius and keen eyes become a curse — the animal spends its life watching instead of eating.",
      min: 0,
      max: 600,
      step: 10,
      format: int,
    });
    this.eknob(eat, "flightDistance", {
      label: "Flight distance",
      unit: "m",
      help: "How close a wolf gets before the hard-wired bolt reflex fires. Beyond it the brain decides for itself. A wolf 400 m away is information, not an emergency.",
      min: 0,
      max: 800,
      step: 10,
      format: int,
    });

    const hunt = f.addFolder({ title: "Predation", expanded: false });
    note(
      hunt,
      "Calibrated against field data: 80–90% of real wolf attacks on wild ungulates fail. A wolf wins on the condition of its prey, not on its legs.",
    );
    this.eknob(hunt, "huntBaseOdds", {
      label: "Base kill odds",
      help: "Chance of a catch against a prime, well-fed, evenly-matched deer. ~20% is the Minnesota figure for white-tailed deer.",
      min: 0,
      max: 1,
      step: 0.01,
      format: pct,
    });
    this.eknob(hunt, "huntFrailtyBonus", {
      label: "Bonus vs. weak prey",
      help: "Added odds against a deer that is old, starving or both. This is the mechanism by which predation selects — it removes the failing, which is why wolf-killed deer average 6.5 years and hunter-killed ones 2.5.",
      min: 0,
      max: 1,
      step: 0.01,
      format: pct,
    });
    this.eknob(hunt, "huntPackBonus", {
      label: "Pack bonus",
      help: "Added odds as packmates pile in, with diminishing returns. Two packmates ≈ +20 points, five ≈ +27.",
      min: 0,
      max: 1,
      step: 0.01,
      format: pct,
    });
    this.eknob(hunt, "packHuntRadius", {
      label: "Pack range",
      unit: "m",
      help: "How close a packmate has to be to count toward the hunt.",
      min: 0,
      max: 400,
      step: 10,
      format: int,
    });
    this.eknob(hunt, "huntSizeBonus", {
      label: "Size mismatch",
      help: "How far a size difference can swing the odds, in either direction. Being big is a deer's defence; being big is a wolf's weapon.",
      min: 0,
      max: 1,
      step: 0.01,
      format: pct,
    });
    this.eknob(hunt, "huntSpeedBonus", {
      label: "Speed gap",
      help: "How much the top-speed difference matters. Deliberately small — a wolf is barely faster than a deer — but never zero, or legs become a pure cost to both.",
      min: 0,
      max: 1,
      step: 0.01,
      format: pct,
    });
    this.eknob(hunt, "huntAgilityBonus", {
      label: "Agility gap",
      help: "The same for the difference in turning power.",
      min: 0,
      max: 1,
      step: 0.01,
      format: pct,
    });
    this.eknob(hunt, "energyPerKg", {
      label: "Carcass richness",
      unit: "energy per kg",
      help: "How much a kilogram of prey is worth. With efficiency below, this sets what one deer buys a pack.",
      min: 1,
      max: 60,
      step: 1,
      format: int,
    });
    this.eknob(hunt, "preyEfficiency", {
      label: "Digestive efficiency",
      help: "The share of a carcass a predator actually absorbs. The rest is hide, bone and scavengers.",
      min: 0,
      max: 1,
      step: 0.05,
      format: pct,
    });
    this.eknob(hunt, "killDist", {
      label: "Strike distance",
      unit: "m",
      help: "How close a wolf must close before it can attempt a kill.",
      min: 1,
      max: 40,
      step: 1,
      format: int,
    });
    this.eknob(hunt, "killHandling", {
      label: "Handling time",
      unit: "s",
      help: "Eating and digesting, before it can kill again. This is the brake that stops predators clearing the map — raise it and wolves starve, lower it and they strip the herd.",
      min: 0,
      max: 60,
      step: 1,
      format: int,
    });

    const rep = f.addFolder({ title: "Breeding", expanded: false });
    this.eknob(rep, "breedCooldown", {
      label: "Breeding cooldown",
      unit: "s",
      help: "The wait before an animal can breed again. 90 seconds is one year on the life-history clock — both species breed annually.",
      min: 0,
      max: 300,
      step: 5,
      format: int,
    });

    action(f, "Restore default economy", "Puts every figure in this section back to the shipped value.", () => {
      this.applying = true;
      Object.assign(this.econ, DEFAULT_ECON);
      this.pane.refresh();
      this.applying = false;
      this.markCustom();
      this.pushEcon();
    });
  }

  // --- Experiments ----------------------------------------------------------

  private buildExperiments(pane: Pane, cb: ControlCallbacks): void {
    const f = pane.addFolder({ title: "Experiments", expanded: false });
    note(
      f,
      "Saves the seed, every setting and the current charts to the server, so a run started on one machine can be reopened on another.",
    );
    const b = f.addBinding(this.exp, "name", { label: "Name" });
    after(b, helpEl("What to file this run under."));
    action(f, "Save to server", "Stores the run as it stands right now.", () => cb.onSave(this.exp.name));
    action(f, "Load…", "Lists saved runs and restarts with the one you pick.", cb.onLoad);
  }
}

// --- Styles ------------------------------------------------------------------
// Injected here rather than in style.css so this module stays self-contained
// and can't drift out of sync with the markup it generates. Only `.eco-*`
// classes are touched — Tweakpane's own styling is left alone. Colours come
// from the design tokens, with literal fallbacks so the panel still reads if
// the stylesheet hasn't loaded.

const STYLE_ID = "eco-controls-style";

function injectStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
.eco-pane {
  --eco-sans: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  --eco-mono: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
.eco-pane .eco-help {
  margin: -1px 0 7px 2px;
  padding: 0 2px 0 7px;
  border-left: 1px solid var(--line, #2a3327);
  font: 400 10px/1.4 var(--eco-sans);
  color: var(--muted, #8a9580);
}
.eco-pane.eco-nohelp .eco-help,
.eco-pane.eco-nohelp .eco-note { display: none; }
.eco-pane .eco-unit {
  display: inline-block;
  margin: 0 5px 1px 0;
  padding: 0 4px;
  border-radius: var(--r-sm, 6px);
  background: var(--panel-2, #1a2017);
  color: var(--dim, #5f6b57);
  font: 400 9px/1.5 var(--eco-mono);
  white-space: nowrap;
  vertical-align: 1px;
}
.eco-pane .eco-note {
  margin: 2px 0 8px;
  padding: 6px 8px;
  border-radius: var(--r-sm, 6px);
  background: var(--panel-2, #1a2017);
  border: 1px solid var(--line, #2a3327);
  font: 400 10px/1.45 var(--eco-sans);
  color: var(--muted, #8a9580);
}
/* The separator blades used purely as insertion anchors. */
.eco-pane .eco-anchor { display: none; }
.eco-pane .eco-block { margin: 2px 0 0; }

.eco-pane .eco-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 2px 0 6px;
}
.eco-pane .eco-chip {
  flex: 1 1 auto;
  min-height: 26px;
  padding: 4px 9px;
  border: 1px solid var(--line, #2a3327);
  border-radius: var(--r-sm, 6px);
  background: var(--panel-2, #1a2017);
  color: var(--ink, #dde5d4);
  font: 500 10.5px/1.2 var(--eco-sans);
  letter-spacing: 0.01em;
  cursor: pointer;
  touch-action: manipulation;
  transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}
.eco-pane .eco-chip:hover { border-color: var(--accent, #6fbf5c); }
.eco-pane .eco-chip:focus-visible { outline: 1px solid var(--accent, #6fbf5c); outline-offset: 1px; }
.eco-pane .eco-chip.is-active {
  background: var(--accent, #6fbf5c);
  border-color: var(--accent, #6fbf5c);
  color: var(--bg, #0b0f0a);
  font-weight: 600;
}
.eco-pane .eco-desc {
  min-height: 2.6em;
  margin: 0 0 6px 2px;
  padding: 0 2px 0 7px;
  border-left: 2px solid var(--accent, #6fbf5c);
  font: 400 10.5px/1.4 var(--eco-sans);
  color: var(--ink, #dde5d4);
}
`;
  document.head.appendChild(s);
}
